import { useEffect, useRef, useState, useMemo } from 'react';
import { toProxiedWpUrl } from '@/utils/wpMediaUrl';
import className from 'classnames/bind';
import { getHighlightMarkup } from '@/core/utils';
import { FeatureExhibitsInteractivesFragment } from '@graphqlTypes';
import { Button, Container, CursorFollower, Image, RhLink, TypePill, HeadlineAnim } from '@components';
import { useInView } from 'motion/react';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
// import { AnimVals } from '@/utils/animUtils';

import styles from './SingleExhibitsInteractives.module.scss';
import { ExhibitData } from '../utils';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
const cx = className.bind(styles);

interface SingleExhibitsInteractivesProps {
  exhibit?: ExhibitData;
  altExhibit?: FeatureExhibitsInteractivesFragment['exhibits'][0];
  headline?: string;
  largeTitle?: boolean;
  pillLabel?: string;
}

export default function SingleExhibitsInteractives({ exhibit, headline, largeTitle, pillLabel }: SingleExhibitsInteractivesProps) {
  const { labels } = usePageWrapperData();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoInView = useInView(videoRef, { amount: 0.25 });
  const componentInView = useInView(containerRef, { amount: 0.25 });
  const anchorRef = useRef<HTMLElement>(null);
  const hasAttachedVideo = useRef(false);
  const { title, mediaType, videoThumbnail, videoUrl, image, excerpt, uri, linkTarget, labelOverride } = exhibit || {};
  const isVideo = mediaType === "video";
  const headlineMarkup = getHighlightMarkup(headline);
  const ctaLabel = labelOverride || labels?.exhibitCarouselCta;
  const Tag = uri ? RhLink : 'div';
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();

  // CursorFollower visibility control
  const [showCursorFollower, setShowCursorFollower] = useState(false);

  // Track if component has animated in (only animate once)
  const hasAnimatedIn = useRef(false);

  // Animation reveal control
  const isRevealEnabled = true; // Can be made a prop if needed

  // Determine if component should show animated state
  const effectiveIsInView = useMemo(() => {
    return componentInView;
  }, [componentInView]);

  // Mark as animated once the animation should have completed
  useEffect(() => {
    if (effectiveIsInView && !hasAnimatedIn.current) {
      hasAnimatedIn.current = true;
    }
  }, [effectiveIsInView]);

  useEffect(() => {
    if (videoInView && !hasAttachedVideo.current && isVideo && videoRef.current) {
      hasAttachedVideo.current = true;
      videoRef.current.src = videoUrl;
    }
    if (videoInView) {
      videoRef.current?.play();
    } else {
      videoRef.current?.pause();
    }
  }, [videoInView])

  // Show cursor follower when in view (desktop only)
  useEffect(() => {
    if (mounted && isReady && isDesktop && componentInView && uri) {
      setShowCursorFollower(true);
    } else {
      setShowCursorFollower(false);
    }
  }, [mounted, isReady, isDesktop, componentInView, uri]);

  // -------------------- Render

  return (
    <section className={cx(["feature-exhibits-interactives"])}>
      <Container>
        { headlineMarkup && (
            <Container className={cx("headline")}>
              <h3 className={cx([largeTitle ? "title-uc-xl" : "title-uc-l"])} dangerouslySetInnerHTML={{ __html: headlineMarkup }} />
            </Container>
          )
        }
         <Tag href={uri || ''} target={linkTarget || "_self"} className={cx("slide")} ref={anchorRef as any}>
            <div className={cx("container")} ref={containerRef}>
              <div className={cx("container-top")}>
                { isVideo ? (
                  <video
                    className={cx("video")}
                    loop
                    muted
                    playsInline
                    ref={videoRef}
                    poster={toProxiedWpUrl(videoThumbnail.node.mediaItemUrl)}
                  />
                ) : (
                  <Image className={cx("image")} image={image} />
                )}
                <div className={cx("container-top-content")}>
                  {/* Pill */}
                  {pillLabel && <TypePill className={cx("pill", { "is-new": pillLabel })} type="new" copy={pillLabel}  /> }
                  {/* Headline */}
                  <div className={cx("headline-wrapper")}>
                    <HeadlineAnim
                      tag="h3"
                      text={title}
                      className={cx(["display-xl", "slide-headline"])}
                      isRevealEnabled={isRevealEnabled}
                      isInView={effectiveIsInView || hasAnimatedIn.current}
                      animInDelay={0.1}
                      isCenterAlign={true}
                      maxCharsPerWord={12}
                    />
                  </div>
                </div>
                <div className={cx("learn-more")}>
                  <Button label={ctaLabel} asDiv collapseOnMobile={true} />
                </div>
              </div>
              <div className={cx("container-bottom")}>
                <p className={cx("text-m", "copy")} dangerouslySetInnerHTML={{ __html: excerpt }} />
              </div>
            </div>
            {/* CursorFollower */}
            { uri && isReady && isDesktop && (
              <CursorFollower
                container={anchorRef}
                circleText={ctaLabel}
                isVisible={showCursorFollower}
                mouseActive={showCursorFollower}
              />
            )}
          </Tag>
      </Container>
    </section>
  )
}